// Deterministic 2D math for the shared sim. Hot-path helpers write into caller-owned `out`
// objects so stepping allocates nothing. Only IEEE-identical builtins are used (§18.4).
import type { Vec2 } from './types';

export const TAU = Math.PI * 2;
/** Multiply degrees by DEG to get radians. */
export const DEG = Math.PI / 180;

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Moves `v` toward `target` by at most `maxDelta`. */
export function approach(v: number, target: number, maxDelta: number): number {
  return v < target ? Math.min(v + maxDelta, target) : Math.max(v - maxDelta, target);
}

export function v2(x = 0, y = 0): Vec2 {
  return { x, y };
}

export function setV(out: Vec2, x: number, y: number): Vec2 {
  out.x = x;
  out.y = y;
  return out;
}

export function copyV(out: Vec2, a: Readonly<Vec2>): Vec2 {
  out.x = a.x;
  out.y = a.y;
  return out;
}

export function len(x: number, y: number): number {
  return Math.hypot(x, y);
}

export function lenSq(x: number, y: number): number {
  return x * x + y * y;
}

export function dist(ax: number, ay: number, bx: number, by: number): number {
  return Math.hypot(bx - ax, by - ay);
}

export function distSq(ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  return dx * dx + dy * dy;
}

export function dot(ax: number, ay: number, bx: number, by: number): number {
  return ax * bx + ay * by;
}

/** z-component of the 3D cross product; > 0 when b is counter-clockwise from a. */
export function cross(ax: number, ay: number, bx: number, by: number): number {
  return ax * by - ay * bx;
}

/** Writes the unit vector of (x, y) into `out`; a zero vector stays (0, 0). */
export function normalizeV(out: Vec2, x: number, y: number): Vec2 {
  const l = Math.hypot(x, y);
  if (l === 0) return setV(out, 0, 0);
  return setV(out, x / l, y / l);
}

/** Writes (x, y) scaled down to length `max` if longer. */
export function clampLength(out: Vec2, x: number, y: number, max: number): Vec2 {
  const l2 = x * x + y * y;
  if (l2 <= max * max) return setV(out, x, y);
  const s = max / Math.sqrt(l2);
  return setV(out, x * s, y * s);
}

/** Velocity steering (§2.3): `v += clampLength(target − v, maxDelta)`, in place. */
export function approachVec(v: Vec2, tx: number, ty: number, maxDelta: number): Vec2 {
  const dx = tx - v.x;
  const dy = ty - v.y;
  const l2 = dx * dx + dy * dy;
  if (l2 <= maxDelta * maxDelta) return setV(v, tx, ty);
  const s = maxDelta / Math.sqrt(l2);
  return setV(v, v.x + dx * s, v.y + dy * s);
}

export function rotateV(out: Vec2, x: number, y: number, angle: number): Vec2 {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  return setV(out, x * c - y * s, x * s + y * c);
}

/** Angle of (x, y) in radians, 0 = +x, π/2 = +y. */
export function angleOf(x: number, y: number): number {
  return Math.atan2(y, x);
}

export function fromAngle(out: Vec2, angle: number, length = 1): Vec2 {
  return setV(out, Math.cos(angle) * length, Math.sin(angle) * length);
}

/** Wraps an angle into (−π, π]. */
export function wrapAngle(a: number): number {
  let r = a % TAU;
  if (r <= -Math.PI) r += TAU;
  else if (r > Math.PI) r -= TAU;
  return r;
}

/** Signed shortest rotation from `a` to `b`, in (−π, π]. */
export function angleDiff(a: number, b: number): number {
  return wrapAngle(b - a);
}

/** Turns angle `a` toward `target` by at most `maxStep` radians; result wrapped to (−π, π]. */
export function rotateToward(a: number, target: number, maxStep: number): number {
  const d = angleDiff(a, target);
  if (Math.abs(d) <= maxStep) return wrapAngle(target);
  return wrapAngle(a + (d > 0 ? maxStep : -maxStep));
}

/** Unsigned angle between two non-zero vectors, in [0, π]. */
export function angleBetween(ax: number, ay: number, bx: number, by: number): number {
  return Math.abs(Math.atan2(cross(ax, ay, bx, by), dot(ax, ay, bx, by)));
}

/** Writes the closest point to P on segment AB into `out` and returns its parameter t ∈ [0, 1]. */
export function closestOnSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number, out: Vec2): number {
  const abx = bx - ax;
  const aby = by - ay;
  const l2 = abx * abx + aby * aby;
  const t = l2 === 0 ? 0 : clamp01(((px - ax) * abx + (py - ay) * aby) / l2);
  setV(out, ax + abx * t, ay + aby * t);
  return t;
}

export function distToSegmentSq(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const abx = bx - ax;
  const aby = by - ay;
  const l2 = abx * abx + aby * aby;
  const t = l2 === 0 ? 0 : clamp01(((px - ax) * abx + (py - ay) * aby) / l2);
  const dx = ax + abx * t - px;
  const dy = ay + aby * t - py;
  return dx * dx + dy * dy;
}

/**
 * Swept test: earliest t ∈ [0, 1] at which a point moving from A to B is within `r` of C,
 * or −1 if it never is. For a moving circle against a circle, pass the sum of both radii.
 */
export function segmentCircleHit(ax: number, ay: number, bx: number, by: number, cx: number, cy: number, r: number): number {
  const fx = ax - cx;
  const fy = ay - cy;
  const c = fx * fx + fy * fy - r * r;
  if (c <= 0) return 0;
  const dx = bx - ax;
  const dy = by - ay;
  const a = dx * dx + dy * dy;
  if (a === 0) return -1;
  const b = 2 * (fx * dx + fy * dy);
  const disc = b * b - 4 * a * c;
  if (disc < 0) return -1;
  const t = (-b - Math.sqrt(disc)) / (2 * a);
  return t >= 0 && t <= 1 ? t : -1;
}
